// SPDX-License-Identifier: Elastic-2.0
/**
 * SuggestionsListPage — `/suggestions` (T-027).
 *
 * Standalone full-feed surface for AI suggestions. Where the dashboard
 * `<SuggestionStrip />` (T-022) only shows the single highest-confidence
 * pending suggestion, this page lists every suggestion the user can act
 * on (pending) plus any settled rows the gateway still surfaces
 * (accepted / dismissed) — see functional spec § 4.5.
 *
 * Composition (no new presentational primitives — everything reuses
 * Group-5/6 components):
 *
 *   - `<SuggestionCard />` (T-022) — per-suggestion render. The card
 *     already owns the three visual states (pending / accepted /
 *     dismissed); we just toggle the `accepted` / `dismissed` props
 *     based on the suggestion's status.
 *   - `<SuggestionApprovalModal />` (T-025) — opened in `"approve"`
 *     mode from the "Yes" click on a pending row, or in `"view"` mode
 *     from a "More info" click on any row (read-only summary).
 *   - `<Page />` + `<Banner />` + `<Button />` — chrome primitives.
 *
 * Status field handling (forward-compat)
 * ──────────────────────────────────────
 * The generated `Suggestion` model doesn't expose a `status` field at
 * MVP — F-300's locked OpenAPI surface only returns pending rows
 * (verified against `web/src/api/gateway/services/SuggestionsService.ts`).
 * So in production today the only filter that ever has results is
 * "pending" / "all" — the "approved" / "dismissed" chips will show their
 * empty state until F-300 broadens the envelope.
 *
 * We still implement the full filter+sort logic now because:
 *   1. The functional spec mandates all four chips at v0.1, and
 *   2. When F-300 adds the `status` discriminator (which the existing
 *      `isPending()` helper in `useSuggestions.ts` already anticipates),
 *      this page will surface them without further changes.
 *
 * Reactivate link: hidden — F-300 has no "reactivate" endpoint in the
 * locked OpenAPI surface. The i18n key exists so when/if F-300 adds the
 * endpoint we can light the link up without another i18n round-trip.
 *
 * Routing
 * ───────
 * Wired by the router (T-004 + Group 7 swap) at `/suggestions`. The
 * page renders inside `<DashboardLayout />`, so the persistent sidebar
 * + topbar already surround it — we own only the content frame.
 */
import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { Suggestion } from "@/api/gateway/models/Suggestion";
import { useAuth } from "@/api/auth-store";
import { Banner } from "@/components/Banner/Banner";
import { Button } from "@/components/Button";
import { Page } from "@/components/Page/Page";
import { ROUTES } from "@/routing/routes";

import { SuggestionApprovalModal } from "./SuggestionApprovalModal";
import { SuggestionCard } from "./SuggestionCard";
import {
  useDismissSuggestion,
  useSuggestions,
} from "./useSuggestions";
import styles from "./SuggestionsListPage.module.css";

/**
 * Possible filter chip values. `"all"` is the default and unions all the
 * other states; the rest map onto a suggestion's status — note the
 * chip-vs-status copy mismatch: the chip is "Approved" (verb tense the
 * user understands), but the underlying status field is `accepted`
 * (matching the SuggestionCard's `accepted` prop and the gateway's
 * vocabulary). `FILTER_TO_STATUS` keeps the two surfaces aligned in one
 * place.
 */
type FilterChip = "all" | "pending" | "approved" | "dismissed";
type SuggestionStatus = "pending" | "accepted" | "dismissed";

const FILTER_CHIPS: readonly FilterChip[] = ["all", "pending", "approved", "dismissed"];

const FILTER_TO_STATUS: Record<Exclude<FilterChip, "all">, SuggestionStatus> = {
  pending: "pending",
  approved: "accepted",
  dismissed: "dismissed",
};

/**
 * Resolve a suggestion's status. The generated model doesn't expose the
 * field yet (F-300 only emits pending rows today), so we defensively
 * peek at the runtime shape and default to `"pending"` — mirroring the
 * `isPending()` helper in `useSuggestions.ts` so the two stay in sync
 * when F-300 adds the discriminator.
 */
function getStatus(suggestion: Suggestion): SuggestionStatus {
  const raw = (suggestion as Suggestion & { status?: string }).status;
  if (raw === "accepted" || raw === "dismissed") return raw;
  return "pending";
}

/**
 * Apply the active chip filter, then order so pending suggestions float
 * to the top by confidence and settled rows fall behind by recency.
 *
 * Splitting into two arrays + concatenating keeps the comparison
 * pairwise-stable (a single `.sort()` would have to encode the
 * pending-first rule via composite keys, which is harder to reason
 * about — and harder to test, since the test asserts the exact order).
 */
function filterAndSort(
  suggestions: readonly Suggestion[],
  filter: FilterChip,
): Suggestion[] {
  const filtered =
    filter === "all"
      ? [...suggestions]
      : suggestions.filter((s) => getStatus(s) === FILTER_TO_STATUS[filter]);

  const pending: Suggestion[] = [];
  const settled: Suggestion[] = [];
  for (const s of filtered) {
    if (getStatus(s) === "pending") {
      pending.push(s);
    } else {
      settled.push(s);
    }
  }

  pending.sort((a, b) => b.confidence - a.confidence);
  settled.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));

  return [...pending, ...settled];
}

/**
 * Minimal skeleton row — mirrors the SuggestionCard footprint while the
 * query is loading. Three are rendered (`SKELETON_COUNT`); the count
 * matches what `<DashboardPage />` does for the dashboard, keeping the
 * visual rhythm consistent across the app.
 */
const SKELETON_COUNT = 3;

function SkeletonRow({ index }: { index: number }): ReactElement {
  return (
    <div
      key={index}
      className={styles.skeleton}
      aria-hidden="true"
      data-testid="suggestions-skeleton"
    />
  );
}

export function SuggestionsListPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Tier gating (D3, F-516) — mirrors the voice-control paid-feature
  // pattern (`VoiceSettingsSection` / `VoicePage`): Plus/Pro are entitled
  // to cloud-powered suggestions; everyone else (Free, account-less
  // local-only, unresolved tier) sees a non-blocking upgrade banner. We
  // read tier/mode straight from the auth store and fire NO extra network
  // request for non-paid users — the banner is purely client-side, so a
  // Free caller never hits the gateway's 402.
  const tier = useAuth((s) => s.tier);
  const mode = useAuth((s) => s.mode);
  const isPaid = tier === "plus" || tier === "pro";
  const isLocalOnly = mode !== "cloud";

  // CTA target mirrors `local_only_blocked` (PaymentRequiredError):
  // account-less/local-only users can't checkout in place, so we deep-link
  // into the pairing/connect-account flow (same destination + querystring
  // idiom as `VoicePage`'s activate-cloud); paired Free users go to the
  // plan/checkout surface (same as `VoiceSettingsSection`).
  const handleUpgrade = useCallback((): void => {
    if (isLocalOnly) {
      navigate("/onboarding/account?reauth=true&from=suggestions");
    } else {
      navigate(ROUTES.PLAN);
    }
  }, [isLocalOnly, navigate]);

  const [filter, setFilter] = useState<FilterChip>("all");
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  // The approval modal has two modes — "approve" (Yes click on a pending
  // card) and "view" (More info on any card). We track the mode alongside
  // the selected suggestion so the modal can be reused without remounting.
  const [modalMode, setModalMode] = useState<"approve" | "view">("approve");

  const query = useSuggestions();
  const dismiss = useDismissSuggestion();

  const visible = useMemo(
    () => filterAndSort(query.data?.suggestions ?? [], filter),
    [query.data, filter],
  );

  const handleCloseModal = useCallback(() => setSelectedSuggestion(null), []);

  const handleAccept = useCallback((suggestion: Suggestion): void => {
    setModalMode("approve");
    setSelectedSuggestion(suggestion);
  }, []);

  const handleMoreInfo = useCallback((suggestion: Suggestion): void => {
    setModalMode("view");
    setSelectedSuggestion(suggestion);
  }, []);

  const handleDismiss = useCallback(
    (suggestion: Suggestion): void => {
      dismiss.mutate(suggestion.id);
    },
    [dismiss],
  );

  const renderBody = (): ReactElement => {
    if (query.isPending) {
      return (
        <div
          className={styles.list}
          aria-busy="true"
          aria-label={t("suggestions_page.loading")}
          data-testid="suggestions-loading"
        >
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <SkeletonRow key={i} index={i} />
          ))}
        </div>
      );
    }

    if (query.isError) {
      return (
        <div className={styles.errorSlot} data-testid="suggestions-error">
          <Banner kind="error" message={t("suggestions_page.error_title")} />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
            data-testid="suggestions-error-retry"
          >
            {t("suggestions_page.error_retry")}
          </Button>
        </div>
      );
    }

    if (visible.length === 0) {
      return (
        <p
          className={styles.empty}
          data-testid="suggestions-empty"
          data-empty-filter={filter}
        >
          {t(`suggestions_page.empty.${filter}`)}
        </p>
      );
    }

    return (
      <div className={styles.list} data-testid="suggestions-list">
        {visible.map((suggestion) => {
          const status = getStatus(suggestion);
          const isPending = status === "pending";
          return (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              accepted={status === "accepted"}
              dismissed={status === "dismissed"}
              // Settled rows are read-only — the card hides its action
              // row entirely when `accepted` or `dismissed`. We still
              // wire the handlers because the prop is required; they
              // simply never fire.
              onAccept={isPending ? () => handleAccept(suggestion) : () => {}}
              onDismiss={isPending ? () => handleDismiss(suggestion) : () => {}}
              onMoreInfo={() => handleMoreInfo(suggestion)}
              busy={isPending && dismiss.isPending && dismiss.variables === suggestion.id}
            />
          );
        })}
      </div>
    );
  };

  return (
    <Page title={t("suggestions_page.title")} data-testid="suggestions-page">
      <p className={styles.caption}>{t("suggestions_page.caption")}</p>

      {!isPaid && (
        <div className={styles.cloudUpgrade} data-testid="suggestions-cloud-upgrade">
          <Banner
            kind="info"
            title={t("suggestions_page.cloud_upgrade_cta.title")}
            message={t("suggestions_page.cloud_upgrade_cta.body")}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpgrade}
            data-testid="suggestions-cloud-upgrade-cta"
          >
            {t(
              isLocalOnly
                ? "suggestions_page.cloud_upgrade_cta.cta_local_only"
                : "suggestions_page.cloud_upgrade_cta.cta",
            )}
          </Button>
        </div>
      )}

      <div
        className={styles.filters}
        role="tablist"
        aria-label={t("suggestions_page.title")}
        data-testid="suggestions-filters"
      >
        {FILTER_CHIPS.map((chip) => {
          const active = chip === filter;
          return (
            <button
              key={chip}
              type="button"
              role="tab"
              aria-selected={active}
              className={[styles.chip, active ? styles.chipActive : ""]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setFilter(chip)}
              data-testid={`suggestions-filter-${chip}`}
              data-active={active}
            >
              {t(`suggestions_page.filters.${chip}`)}
            </button>
          );
        })}
      </div>

      {renderBody()}

      <SuggestionApprovalModal
        suggestion={selectedSuggestion}
        open={selectedSuggestion !== null}
        onClose={handleCloseModal}
        mode={modalMode}
      />
    </Page>
  );
}

export default SuggestionsListPage;
